import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Loader2, PackageOpen, ShieldCheck, Sparkles, FileDown, MapPin, BadgeCheck, HardDriveDownload, Power, X } from 'lucide-react';
import { BUSINESS, getProducts, getProductFacets, HERO_IMAGES } from '../config';
import ProductCard from '../components/ProductCard';
import FilterBar from '../components/FilterBar';
import SidebarFilters from '../components/SidebarFilters';
import DensityControl from '../components/DensityControl';
import Pagination from '../components/Pagination';

// Ladder responsive « Auto » (4 col en 2K, 5 en 4K, jusqu'à 7 en 8K). Chaîne LITTÉRALE
// pour que Tailwind génère les classes. Au-delà, le réglage manuel (1→8 col, stepper −/+)
// est appliqué en style inline — catalogue = SPA (react-snap exclude), pas d'hydratation,
// donc style dynamique sans risque. Cf refonte filtres 2026-06-01.
const AUTO_GRID = 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 4xl:grid-cols-5 5xl:grid-cols-6 6xl:grid-cols-7';

// Accroche de l'en-tête, SELON LE FILTRE. Elle annonçait « garantie 6 mois pièces
// et main-d'œuvre » sans aucune condition : c'était faux sur la vue « neuf », et
// sur le reconditionné cela laissait croire que 6 mois était toute la protection
// du client, alors que la garantie légale de conformité est de 2 ans dans les deux
// cas (CGV article 9).
//
// Jamais d'addition « 2 ans + 6 mois » : les deux protections sont concurrentes,
// pas consécutives.
//
// Exportée pour être testée seule : la monter dans la page entière exigerait de
// doubler le catalogue, et on ne vérifierait plus le texte mais le décor.
export function catalogueBaseline(condition) {
  if (condition === 'neuf') {
    return 'Matériel neuf sélectionné par notre atelier, couvert 2 ans par la garantie '
      + 'légale de conformité, retrait en boutique à Meaux.';
  }
  return 'Sélection vérifiée par notre atelier. Tests hardware complets, 6 mois pièces '
    + 'et main-d’œuvre sur le reconditionné, en plus de la garantie légale de conformité '
    + 'de 2 ans, retrait en boutique à Meaux.';
}

// Compteur d'en-tête. Il affichait « 6 / Mois de garantie » sur le reconditionné,
// en corps 5xl : le chiffre le plus visible de la page disait la protection la plus
// courte. La garantie légale vaut 2 ans pour tout le catalogue, neuf comme
// reconditionné — le compteur ne dépend donc plus du filtre.
export const CATALOGUE_WARRANTY_STAT = {
  value: '2',
  unit: 'Ans',
  label: 'de garantie légale',
};

export default function Catalogue() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  const filters = {
    search: searchParams.get('search') || '',
    category: searchParams.get('category') || '',
    condition: searchParams.get('condition') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    brand: searchParams.get('brand') || '',
    cpu: searchParams.get('cpu') || '',
    ramMin: searchParams.get('ramMin') || '',
    screenMin: searchParams.get('screenMin') || '',
    screenMax: searchParams.get('screenMax') || '',
    printerTech: searchParams.get('printerTech') || '',
    printerColor: searchParams.get('printerColor') || '',
    printerMfp: searchParams.get('printerMfp') || '',
    screenRes: searchParams.get('screenRes') || '',
  };
  const [profile, setProfile] = useState('');
  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem('catalogue_density') || 'auto'; } catch { return 'auto'; }
  });
  const changeDensity = (d) => {
    setDensity(d);
    try { localStorage.setItem('catalogue_density', d); } catch { /* stockage indispo : on ignore */ }
  };
  const [facets, setFacets] = useState({ brands: [], cpu_families: [], ram_options: [], categories: [], conditions: [], price_buckets: [], price_min: 0, price_max: 0, screen_values: [], printer_tech: [], printer_color: [], printer_mfp: [], screen_resolutions: [] });

  const FILTER_KEYS = ['search', 'category', 'condition', 'minPrice', 'maxPrice', 'brand', 'cpu', 'ramMin', 'screenMin', 'screenMax', 'printerTech', 'printerColor', 'printerMfp', 'screenRes'];
  const setFilters = (newFilters) => {
    const params = {};
    FILTER_KEYS.forEach((k) => { if (newFilters[k]) params[k] = newFilters[k]; });
    setSearchParams(params, { replace: true });
    setPage(1);
  };
  const patchFilters = (patch) => setFilters({ ...filters, ...patch });
  const handlePageChange = (next) => {
    setPage(next);
    // Remonter JUSTE sous la barre de filtres sticky. On mesure son bas réel (header +
    // barre, hauteur variable mobile/desktop) plutôt qu'un offset magique qui masquerait
    // la 1re ligne sous la barre. La grille existe encore au moment du clic (loading=false).
    const grid = document.querySelector('[data-testid="product-grid"]');
    if (!grid) return;
    const bar = document.querySelector('[data-testid="filter-bar"]');
    const stickyBottom = bar ? bar.getBoundingClientRect().bottom : 160;
    const y = window.scrollY + grid.getBoundingClientRect().top - stickyBottom - 16;
    window.scrollTo({ top: Math.max(y, 0), behavior: 'smooth' });
  };
  const onProfile = (key, preset) => {
    setProfile(key);
    if (preset) patchFilters(preset);
  };

  const hasActiveFilters = ['category', 'condition', 'minPrice', 'maxPrice', 'brand', 'cpu', 'ramMin', 'screenMin', 'printerTech', 'printerColor', 'printerMfp', 'screenRes', 'search'].some((k) => filters[k]);
  const clearAllFilters = () => { setProfile(''); setFilters({}); };

  // Facettes + COMPTEURS pour le périmètre courant. On envoie TOUS les filtres actifs
  // (le backend ignore la propre sélection de chaque facette = exclude-own) et on
  // recalcule à chaque changement → compteurs dynamiques.
  useEffect(() => {
    const p = {};
    if (filters.category) p.category = filters.category;
    if (filters.condition) p.condition = filters.condition;
    if (filters.minPrice) p.min_price = filters.minPrice;
    if (filters.maxPrice) p.max_price = filters.maxPrice;
    if (filters.brand) p.brand = filters.brand;
    if (filters.cpu) p.cpu = filters.cpu;
    if (filters.ramMin) p.ram_min = filters.ramMin;
    if (filters.screenMin) p.screen_min = filters.screenMin;
    if (filters.screenMax) p.screen_max = filters.screenMax;
    if (filters.printerTech) p.printer_tech = filters.printerTech;
    if (filters.printerColor) p.printer_color = filters.printerColor;
    if (filters.printerMfp) p.printer_mfp = filters.printerMfp;
    if (filters.screenRes) p.screen_res = filters.screenRes;
    if (filters.search) p.search = filters.search;
    // Mémorise la requête filtres pour la nav Suivant/Précédent des fiches (même params
    // que /products/ids) — la fiche détail la relit sans repasser par les filtres.
    try { sessionStorage.setItem('catalogue_q', new URLSearchParams(p).toString()); } catch { /* stockage indispo */ }
    getProductFacets(p).then((d) => setFacets(d || {})).catch(() => {});
  }, [filters.category, filters.condition, filters.minPrice, filters.maxPrice, filters.brand, filters.cpu, filters.ramMin, filters.screenMin, filters.screenMax, filters.printerTech, filters.printerColor, filters.printerMfp, filters.screenRes, filters.search]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: page.toString(), limit: '20' };
      if (filters.search) params.search = filters.search;
      if (filters.category) params.category = filters.category;
      if (filters.condition) params.condition = filters.condition;
      if (filters.minPrice) params.min_price = filters.minPrice;
      if (filters.maxPrice) params.max_price = filters.maxPrice;
      if (filters.brand) params.brand = filters.brand;
      if (filters.cpu) params.cpu = filters.cpu;
      if (filters.ramMin) params.ram_min = filters.ramMin;
      if (filters.screenMin) params.screen_min = filters.screenMin;
      if (filters.screenMax) params.screen_max = filters.screenMax;
      if (filters.printerTech) params.printer_tech = filters.printerTech;
      if (filters.printerColor) params.printer_color = filters.printerColor;
      if (filters.printerMfp) params.printer_mfp = filters.printerMfp;
      if (filters.screenRes) params.screen_res = filters.screenRes;
      const data = await getProducts(params);
      setProducts(data.products || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [filters.search, filters.category, filters.condition, filters.minPrice, filters.maxPrice, filters.brand, filters.cpu, filters.ramMin, filters.screenMin, filters.screenMax, filters.printerTech, filters.printerColor, filters.printerMfp, filters.screenRes, page]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const catalogueUrl = filters.category
    ? `${BUSINESS.url}/catalogue?category=${encodeURIComponent(filters.category)}`
    : `${BUSINESS.url}/catalogue`;
  const seoTitle = filters.search
    ? `Recherche : ${filters.search} — ${BUSINESS.name}`
    : filters.condition === 'reconditionne'
      ? `PC reconditionnés — ${BUSINESS.name}`
      : filters.condition === 'neuf'
        ? `PC neufs — ${BUSINESS.name}`
        : `Catalogue — ${BUSINESS.name}`;
  const seoDescription = filters.search
    ? `Résultats de recherche pour « ${filters.search} » dans le catalogue. PC reconditionnés et neufs, retrait en boutique à Meaux.`
    : filters.condition === 'neuf'
      ? `Catalogue PC neufs garantis 2 ans, retrait en boutique à Meaux (77100). Transfert de vos données et mise en service au retrait.`
      : `Catalogue : PC reconditionnés et neufs. Tests hardware complets, garantie légale de 2 ans et 6 mois pièces et main-d'œuvre sur le reconditionné, retrait en boutique à Meaux (77100).`;
  const itemListJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `Catalogue — ${BUSINESS.name}`,
    numberOfItems: total,
    itemListElement: products.slice(0, 20).map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `${BUSINESS.url}/produit/${p.id}`,
      item: {
        '@type': 'Product',
        name: p.name,
        url: `${BUSINESS.url}/produit/${p.id}`,
        offers: {
          '@type': 'Offer',
          price: p.price,
          priceCurrency: 'EUR',
          availability: p.status === 'available' ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        },
      },
    })),
  };

  const heroTitle = filters.condition === 'reconditionne'
    ? <>PC<br /><span className="text-secondary">reconditionnés</span> garantis</>
    : filters.condition === 'neuf'
      ? <>PC<br /><span className="text-secondary">neufs</span> garantis</>
      : <>PC neufs<br /><span className="text-secondary">&amp; reconditionnés</span> garantis</>;

  return (
    <div data-testid="catalogue-page">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={catalogueUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={catalogueUrl} />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <script type="application/ld+json">{JSON.stringify(itemListJsonLd)}</script>
      </Helmet>

      {/* ─── HERO CATALOGUE (fond sombre + image atelier) ─── */}
      <section className="relative bg-primary text-white py-10 md:py-14 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-45"
          style={{
            backgroundImage: `url('${
              filters.condition === 'neuf' ? HERO_IMAGES.desktop : filters.condition === 'reconditionne' ? HERO_IMAGES.laptop : HERO_IMAGES.workshop
            }')`,
          }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/70 to-primary/20" aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <span className="font-accent text-xs uppercase tracking-widest text-secondary">Notre catalogue</span>
              <h1 className="font-heading font-bold text-3xl md:text-5xl uppercase tracking-tight leading-[1.05] mt-2" data-testid="catalogue-title">
                {heroTitle}
              </h1>
              <p className="text-slate-300 mt-4 text-base max-w-xl" data-testid="catalogue-baseline">
                {catalogueBaseline(filters.condition)}
              </p>
            </div>
            <div className="flex items-center gap-6">
              <div>
                <span className="font-heading text-4xl md:text-5xl font-bold text-secondary leading-none">{total}</span>
                <span className="block font-accent text-xs uppercase tracking-widest text-slate-400 mt-1">Produit{total > 1 ? 's' : ''}<br />disponible{total > 1 ? 's' : ''}</span>
              </div>
              <div className="hidden sm:block w-px h-12 bg-white/10"></div>
              <div className="hidden sm:block">
                <span className="font-heading text-4xl md:text-5xl font-bold text-white leading-none">{CATALOGUE_WARRANTY_STAT.value}</span>
                <span className="block font-accent text-xs uppercase tracking-widest text-slate-400 mt-1">{CATALOGUE_WARRANTY_STAT.unit}<br />{CATALOGUE_WARRANTY_STAT.label}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── BARRE FILTRES (fond clair) ─── */}
      {/* Même largeur que la grille produits ci-dessous pour aligner les bords. */}
      <section className="bg-white border-b border-slate-200 sticky top-[8.5rem] md:top-[6.5rem] z-30">
        <div className="max-w-[3840px] mx-auto px-4 sm:px-6 2xl:px-10 py-4">
          <FilterBar filters={filters} onChange={setFilters} />
        </div>
      </section>

      {/* ─── GRILLE PRODUITS + SIDEBAR FILTRES (Vague 3) ─── */}
      <section className="bg-slate-50 py-10 md:py-14 min-h-[40vh]">
        {/* minmax(0,1fr) (et non 1fr) : autorise la colonne produits à descendre
            sous son min-content — sinon la largeur intrinsèque des photos fait
            exploser la piste et déborder le conteneur (cartes géantes). La grille
            s'élargit jusqu'au 4K ; le nb de colonnes monte par paliers (cf grid ci-dessous). */}
        <div className="max-w-[3840px] mx-auto px-4 sm:px-6 2xl:px-10 lg:grid lg:grid-cols-[16rem_minmax(0,1fr)] lg:gap-6">
          <SidebarFilters filters={filters} facets={facets} profile={profile} onProfile={onProfile} onChange={patchFilters} />
          <div>

          {/* Barre de résultats : compteur (à gauche) + réglage d'affichage (à droite),
              séparé des filtres pour qu'on comprenne tout de suite que c'est l'affichage. */}
          <div className="flex items-center justify-between gap-3 mb-4" data-testid="results-toolbar">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <p className="font-accent text-xs uppercase tracking-widest text-slate-500 whitespace-nowrap">
                {loading
                  ? 'Chargement…'
                  : <><span className="text-primary font-semibold tabular-nums">{total}</span> produit{total > 1 ? 's' : ''}</>}
              </p>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-secondary hover:bg-secondary/10 px-2.5 py-1.5 rounded-sm transition-colors whitespace-nowrap"
                  data-testid="clear-filters"
                >
                  <X className="w-3.5 h-3.5" /> Effacer les filtres
                </button>
              )}
            </div>
            <DensityControl value={density} onChange={changeDensity} />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-8 h-8 text-secondary animate-spin" />
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-24" data-testid="catalogue-empty">
              <PackageOpen className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="font-heading text-xl uppercase tracking-wider text-primary mb-2">Aucun produit trouvé</h3>
              <p className="text-slate-600 text-sm">Essayez de modifier vos filtres ou revenez plus tard.</p>
            </div>
          ) : (
            <>
              <div
                className={`grid ${density === 'auto' ? AUTO_GRID : ''} gap-4 md:gap-5`}
                style={density === 'auto' ? undefined : { gridTemplateColumns: `repeat(${parseInt(density, 10) || 4}, minmax(0, 1fr))` }}
                data-testid="product-grid"
              >
                {products.map(p => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>

              <Pagination currentPage={page} totalPages={pages} onPageChange={handlePageChange} />

              <div className="mt-10 text-center">
                <p className="font-accent text-sm uppercase tracking-widest text-slate-500">
                  {products.length} produit{products.length > 1 ? 's' : ''} sur {total} affiché{total > 1 ? 's' : ''}
                </p>
              </div>
            </>
          )}
          </div>
        </div>
      </section>

      {/* ─── POURQUOI / SERVICES (trust block, condition-aware : neuf vs recondi) ─── */}
      <section className="bg-white text-slate-900 py-12 md:py-20 border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="font-accent text-xs uppercase tracking-widest text-secondary">
              {filters.condition === 'neuf' ? 'Avec votre PC neuf' : 'Pourquoi nous choisir'}
            </span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight text-primary mt-2">
              {filters.condition === 'neuf' ? 'Les services METERIS inclus' : 'Ce qui change avec notre atelier'}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {(filters.condition === 'neuf'
              ? [
                  { icon: BadgeCheck, t: 'Garantie 2 ans', d: 'Garantie légale de conformité sur tous nos PC neufs, assurée par la boutique.' },
                  { icon: HardDriveDownload, t: 'Transfert de vos données', d: 'On récupère vos fichiers, photos et logiciels depuis votre ancien poste.' },
                  { icon: Power, t: 'Mise en service au retrait', d: 'Démarrage, connexion internet et comptes : votre PC repart prêt à l\'emploi.' },
                  { icon: ShieldCheck, t: 'Antivirus préinstallé', d: 'Protection installée et configurée avant le retrait, pour partir serein.' },
                ]
              : [
                  { icon: ShieldCheck, t: 'Tests complets', d: 'Hardware, SSD, RAM, OS — chaque PC est audité par notre cellule technique avant mise en vente.' },
                  { icon: Sparkles, t: 'Garantie 6 mois', d: 'Pièces et main-d\'œuvre, en plus de la garantie légale de conformité de 2 ans. Remise en état en boutique.' },
                  { icon: FileDown, t: 'Certificat PDF', d: 'Chaque PC reconditionné livré avec un certificat PDF détaillé accessible par QR code.' },
                  { icon: MapPin, t: 'Retrait à Meaux', d: 'Récupération en boutique, conseil personnalisé sur place. Pas de logistique transporteur.' },
                ]
            ).map((b, i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-sm p-6 hover:border-secondary transition-colors">
                <div className="w-12 h-12 rounded-sm bg-secondary/10 text-secondary flex items-center justify-center mb-4">
                  <b.icon className="w-6 h-6" />
                </div>
                <h3 className="font-heading text-lg uppercase tracking-wider text-primary mb-2">{b.t}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
